import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Bell, AlertTriangle, Clock, CalendarX } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { isAdmin, readCurrentUser } from '../lib/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const READ_KEY = 'align31_alerts_read';

// Required shifts for the 3.1 program. Swing is optional.
const REQUIRED_SHIFTS = new Set(['day', 'evening', 'overnight']);

const SHIFT_LABELS = {
  day: 'Day (7am–3pm)',
  swing: 'Swing (11am–7pm)',
  evening: 'Evening (3pm–11pm)',
  overnight: 'Overnight (11pm–7am)',
};

const PRIORITY_STYLES = {
  high:   { dot: 'bg-red-500',    label: 'High',    text: 'text-red-700 dark:text-red-400' },
  medium: { dot: 'bg-amber-500',  label: 'Medium',  text: 'text-amber-700 dark:text-amber-400' },
  low:    { dot: 'bg-blue-500',   label: 'Low',     text: 'text-blue-700 dark:text-blue-400' },
};

// Build a YYYY-MM-DD string in local time
const toYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getSundayOfWeek = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
};

const NotificationsBell = () => {
  const [alerts, setAlerts] = useState([]);
  const [readIds, setReadIds] = useState(() => {
    try {
      const raw = localStorage.getItem(READ_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [open, setOpen] = useState(false);

  // Persist read state
  useEffect(() => {
    try {
      localStorage.setItem(READ_KEY, JSON.stringify([...readIds]));
    } catch {
      /* ignore */
    }
  }, [readIds]);

  // Fetch + compute alerts; refresh every 60s
  useEffect(() => {
    let cancelled = false;

    const compute = async () => {
      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const sunday = getSundayOfWeek(today);
        const todayStr = toYMD(today);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = toYMD(tomorrow);

        // STAFF: personal notifications only (own shifts + own availability
        // reminders). Never fetch org-wide endpoints — those are admin-only.
        if (!isAdmin(readCurrentUser())) {
          const out = [];
          const [schedRes, availRes] = await Promise.allSettled([
            axios.get(`${API_URL}/api/shifts/my`),
            axios.get(`${API_URL}/api/availability/my`),
          ]);
          if (cancelled) return;
          if (schedRes.status === 'fulfilled') {
            (schedRes.value.data.shifts || []).forEach((s) => {
              if (s.date !== todayStr && s.date !== tomorrowStr) return;
              out.push({
                id: `myshift:${s.date}:${s.shift_type || s.display_name}`,
                priority: 'medium',
                icon: 'shift',
                title: `You work ${s.display_name}${s.start_time ? ` (${s.start_time}–${s.end_time})` : ''}`,
                subtitle: `${s.date === todayStr ? 'Today' : 'Tomorrow'} • ${s.date}`,
                sortKey: `1:${s.date}`,
              });
            });
          }
          if (availRes.status === 'fulfilled') {
            (availRes.value.data.periods || []).forEach((p) => {
              (p.reminders || []).forEach((r) => {
                out.push({
                  id: `avail:${p.period.id}:${r.type}`,
                  priority: r.type === 'past_due' ? 'high' : 'medium',
                  icon: 'task',
                  title: r.message,
                  subtitle: p.period.name,
                  sortKey: `2:${r.type}`,
                });
              });
            });
          }
          out.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
          setAlerts(out);
          return;
        }

        const [shiftsRes, tasksRes, teamRes] = await Promise.all([
          axios.get(`${API_URL}/api/shifts/week/${toYMD(sunday)}`),
          axios.get(`${API_URL}/api/tasks`),
          axios.get(`${API_URL}/api/team?active_only=true`),
        ]);

        if (cancelled) return;

        const teamByName = new Map(teamRes.data.map((m) => [m.name, m]));
        const out = [];

        // (1) Uncovered shifts for today or tomorrow — HIGH
        shiftsRes.data.daily_coverage.forEach((day) => {
          if (day.date !== todayStr && day.date !== tomorrowStr) return;
          day.shifts.forEach((s) => {
            if (s.is_covered) return;
            if (!REQUIRED_SHIFTS.has(s.shift_type)) return;
            const dateLabel = day.date === todayStr ? 'Today' : 'Tomorrow';
            out.push({
              id: `uncovered:${day.date}:${s.shift_type}`,
              priority: 'high',
              icon: 'shift',
              title: `${SHIFT_LABELS[s.shift_type]} uncovered`,
              subtitle: `${dateLabel} • ${day.date}`,
              sortKey: `1:${day.date}:${s.shift_type}`,
            });
          });
        });

        // (2) Tasks past due date (and not completed) — MEDIUM
        tasksRes.data.forEach((t) => {
          if (!t.due_date) return;
          if (t.status === 'completed') return;
          if (t.due_date < todayStr) {
            out.push({
              id: `overdue:${t.id}`,
              priority: 'medium',
              icon: 'task',
              title: t.title,
              subtitle: `Overdue since ${t.due_date}${t.assignee ? ` • ${t.assignee}` : ''}`,
              sortKey: `2:${t.due_date}:${t.id}`,
            });
          }
        });

        // (3) Staff scheduled outside their preferred-shifts ("Not Available") — LOW
        // Only flag for required shifts so we don't spam over optional Swing assignments.
        shiftsRes.data.daily_coverage.forEach((day) => {
          day.shifts.forEach((s) => {
            if (!s.is_covered) return;
            if (!REQUIRED_SHIFTS.has(s.shift_type)) return;
            const member = teamByName.get(s.assigned_rss_name);
            if (!member) return;
            const prefs = member.preferred_shifts || [];
            if (prefs.length === 0) return;       // no preference = available for any
            if (prefs.includes(s.shift_type)) return;
            out.push({
              id: `unavailable:${day.date}:${s.shift_type}:${s.assigned_rss_name}`,
              priority: 'low',
              icon: 'unavailable',
              title: `${s.assigned_rss_name} scheduled outside availability`,
              subtitle: `${SHIFT_LABELS[s.shift_type]} • ${day.date}`,
              sortKey: `3:${day.date}:${s.shift_type}`,
            });
          });
        });

        // Sort: priority order then date
        out.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
        setAlerts(out);
      } catch (err) {
        console.error('Error loading alerts:', err);
      }
    };

    compute();
    const interval = setInterval(compute, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const unreadCount = useMemo(
    () => alerts.filter((a) => !readIds.has(a.id)).length,
    [alerts, readIds]
  );

  const markAlertRead = (id) => {
    setReadIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const markAllRead = () => {
    setReadIds(new Set(alerts.map((a) => a.id)));
  };

  const renderIcon = (kind) => {
    const cls = 'w-4 h-4 mt-0.5 flex-shrink-0';
    if (kind === 'shift') return <CalendarX className={`${cls} text-red-600`} />;
    if (kind === 'task') return <Clock className={`${cls} text-amber-600`} />;
    return <AlertTriangle className={`${cls} text-blue-600`} />;
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notifications-button"
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="notifications-badge"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-[380px] max-w-[92vw] p-0 bg-background"
        data-testid="notifications-dropdown"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <div className="text-sm font-semibold">Alerts</div>
            <div className="text-xs text-muted-foreground">
              {alerts.length} total · {unreadCount} unread
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs text-primary hover:underline"
              data-testid="mark-all-read-button"
            >
              Mark all read
            </button>
          )}
        </div>

        <div className="max-h-[420px] overflow-y-auto" data-testid="alerts-list">
          {alerts.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="no-alerts">
              You're all caught up. No alerts right now.
            </div>
          ) : (
            alerts.map((a) => {
              const isRead = readIds.has(a.id);
              const p = PRIORITY_STYLES[a.priority];
              return (
                <button
                  key={a.id}
                  onClick={() => markAlertRead(a.id)}
                  className={`w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-muted/50 transition-colors flex items-start gap-3 ${
                    isRead ? 'opacity-60' : ''
                  }`}
                  data-testid={`alert-${a.id}`}
                >
                  {renderIcon(a.icon)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <span className={`inline-block w-2 h-2 rounded-full mt-1.5 ${p.dot}`} aria-hidden />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium leading-tight truncate">{a.title}</div>
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">{a.subtitle}</div>
                      </div>
                    </div>
                  </div>
                  <span className={`text-[10px] font-semibold uppercase tracking-wide ${p.text}`}>
                    {p.label}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationsBell;
